import {
  kodplaner_ekkodplaner,
  kodplaner_ekkodplaner_koddelar_kodbegrepp,
  kodplaner_ekkodplaner_koddelar,
} from '../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import { kodbegreppByPosNr } from './KoddelUtils';
import { getYearFromDate } from '../../Utils/dateUtils';

export interface IKodbegreppInfo {
  index: number;
  startPosition: number;
  endPosition: number;
}

const handleParseKodbegreppError = (kodbegreppNamn: string) => {
  throw new Error(`Failed to parse kodbegrepp: ${kodbegreppNamn}`);
};

export const getKodbegreppInfo = (kodBegreppNamn: string): IKodbegreppInfo => {
  const [name, indexString, startPositionString, endPositionString] =
    kodBegreppNamn.split('_');

  if (!name) {
    handleParseKodbegreppError(kodBegreppNamn);
  }

  let index: number = 0;
  let startPosition: number = 0;
  let endPosition: number = 0;

  index = Number.parseInt(indexString, 10);
  startPosition = Number.parseInt(startPositionString, 10);
  endPosition = Number.parseInt(endPositionString, 10);

  if (isNaN(index) || isNaN(startPosition) || isNaN(endPosition)) {
    handleParseKodbegreppError(kodBegreppNamn);
  }

  return { index, startPosition, endPosition };
};

export const kodbegreppEnabled = (
  kodplan: kodplaner_ekkodplaner,
  kodbegreppInfo: IKodbegreppInfo
): boolean => {
  const kodbegrepp = getKodbegrepp(kodplan, kodbegreppInfo);

  if (!kodbegrepp) {
    return false;
  }

  return true;
};

export const getKodbegrepp = (
  kodplan: kodplaner_ekkodplaner,
  kodbegreppInfo: IKodbegreppInfo
): kodplaner_ekkodplaner_koddelar_kodbegrepp | undefined => {
  const koddel = kodplan.koddelar && kodplan.koddelar[kodbegreppInfo.index - 1];

  if (!koddel) {
    return undefined;
  }

  const kodbegrepp = kodbegreppByPosNr(
    kodbegreppInfo.startPosition,
    kodbegreppInfo.endPosition,
    koddel
  );

  return kodbegrepp;
};

export const getKoddelar = (
  kodplan: kodplaner_ekkodplaner | undefined
): kodplaner_ekkodplaner_koddelar[] => {
  return kodplan && kodplan.koddelar ? kodplan.koddelar : [];
};

export const getKoddelarNames = (
  kodplan: kodplaner_ekkodplaner | undefined
): string[] => getKoddelar(kodplan).map(koddel => koddel.namn || '');

export const kodplanIsActiveForYear = (
  kodplan: kodplaner_ekkodplaner,
  year: number
) => {
  const fromYear = getYearFromDate(kodplan.frombokfar);
  const toYear = getYearFromDate(kodplan.tombokfar);

  return year >= fromYear && year <= toYear;
};

export const getKodplanByYear = (
  kodplanList: kodplaner_ekkodplaner[],
  year: number
): kodplaner_ekkodplaner | undefined => {
  const kodplan = kodplanList.find(kodplan =>
    kodplanIsActiveForYear(kodplan, year)
  );

  return kodplan;
};

export enum KoddelTypes {
  Baskonto,
  Verksamhet,
  Omrade,
  Projekt,
  Gava,
}

export const getKoddelByKoddelType = (
  type: KoddelTypes,
  kodplan: kodplaner_ekkodplaner
): kodplaner_ekkodplaner_koddelar | undefined => {
  if (kodplan.koddelar == null) {
    return undefined;
  }

  if (type === KoddelTypes.Baskonto) {
    return kodplan.koddelar[0];
  } else if (type === KoddelTypes.Verksamhet) {
    return kodplan.koddelar.find(koddel => Boolean(koddel.verksamhet));
  } else if (type === KoddelTypes.Omrade) {
    return kodplan.koddelar.find(koddel => Boolean(koddel.omrade));
  } else if (type === KoddelTypes.Projekt) {
    return kodplan.koddelar.find(koddel => Boolean(koddel.projekt));
  } else if (type === KoddelTypes.Gava) {
    return kodplan.koddelar.find(koddel => Boolean(koddel.gava));
  }

  return undefined;
};
